import { RowCrudStatus } from "../../common/types";
import {
  UserWidgetConfig,
  UserWidgetConfigCreateMutationVariables,
  UserWidgetConfigUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { userDisplayId } from "../../user/store/UserCommonStore";
import { dashboardWidgetDisplayId } from "../../dashboardWidget/store/DashboardWidgetCommonStore";
import { safeJsonParse } from "../../../common/utils";

export type UserWidgetConfigValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  __user: string;
  showUser: { displayId: string; id: string } | null;
  __widget: string;
  showWidget: { displayId: string; id: string } | null;
  enabled: boolean;
  position: string;
  configOverride: string;
  displayOrder: number | undefined;
};

export type UserWidgetConfigErrorsType = {
  user?: { message: string; fieldLabel: string };
  widget?: { message: string; fieldLabel: string };
  enabled?: { message: string; fieldLabel: string };
  position?: { message: string; fieldLabel: string };
  configOverride?: { message: string; fieldLabel: string };
  displayOrder?: { message: string; fieldLabel: string };
};

export function UserWidgetConfigValuesInit(): UserWidgetConfigValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    __user: "",
    showUser: null,
    __widget: "",
    showWidget: null,
    enabled: false,
    position: "{}",
    configOverride: "{}",
    displayOrder: undefined,
  };
}

export type UserWidgetConfigType =
  | UserWidgetConfig
  | UserWidgetConfigValuesType;

export function userWidgetConfigDisplayId(
  source: Partial<UserWidgetConfig>,
  _langStore: LangStore
) {
  return (
    (source.user
      ? userDisplayId(source.user, _langStore)
      : _langStore.emptyStringSymbol) +
    "-" +
    (source.widget
      ? dashboardWidgetDisplayId(source.widget, _langStore)
      : _langStore.emptyStringSymbol)
  );
}

export function getUserWidgetConfigValues(
  source: UserWidgetConfigType,
  _langStore: LangStore,
  _duplicated: boolean = false
): UserWidgetConfigValuesType {
  const entityValues = UserWidgetConfigValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : userWidgetConfigDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.showUser =
    "user" in source && source.user
      ? {
          id: source.user.id,
          displayId: userDisplayId(source.user, _langStore),
        }
      : "showUser" in source && source.showUser
      ? { id: source.showUser.id, displayId: source.showUser.displayId }
      : null;
  entityValues.__user = entityValues.showUser?.displayId || "";
  entityValues.showWidget =
    "widget" in source && source.widget
      ? {
          id: source.widget.id,
          displayId: dashboardWidgetDisplayId(source.widget, _langStore),
        }
      : "showWidget" in source && source.showWidget
      ? { id: source.showWidget.id, displayId: source.showWidget.displayId }
      : null;
  entityValues.__widget = entityValues.showWidget?.displayId || "";
  entityValues.enabled = source.enabled || false;
  entityValues.position = source.position;
  entityValues.configOverride = source.configOverride || "";
  entityValues.displayOrder =
    (source.displayOrder as unknown) === "" ||
    source.displayOrder === undefined ||
    source.displayOrder === null
      ? undefined
      : source.displayOrder;
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getUserWidgetConfigUpdateVariables(
  values: UserWidgetConfigValuesType
): UserWidgetConfigUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    userId: values.showUser?.id || "",
    widgetId: values.showWidget?.id || "",
    enabled: values.enabled,
    position: safeJsonParse(values.position, "position"),
    configOverride: safeJsonParse(values.configOverride, "configOverride"),
    displayOrder:
      values.displayOrder === undefined ? null : values.displayOrder,
  };
}

export function getUserWidgetConfigCreateVariables(
  values: UserWidgetConfigValuesType
): UserWidgetConfigCreateMutationVariables {
  return {
    userId: values.showUser?.id || "",
    widgetId: values.showWidget?.id || "",
    enabled: values.enabled,
    position: safeJsonParse(values.position, "position"),
    configOverride: safeJsonParse(values.configOverride, "configOverride"),
    displayOrder:
      values.displayOrder === undefined ? null : values.displayOrder,
  };
}
